// eslint-disable-next-line import/no-unresolved
import api from '@config/axiosConfig';
import { configuredFetchBaseQuery } from '@config/rtkQuery';
import {
  toastFulfilledPromise,
  toastPendingPromise,
  toastRejectedPromise,
} from '@features/promiseHandlers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

export const numberApi = createApi({
  reducerPath: 'numberApi',
  baseQuery: configuredFetchBaseQuery(),
  tagTypes: ['CustomerDids'],
  endpoints: (builder) => ({
    getCustomerNumbers: builder.query({
      query: (arg) => {
        const url = 'customer/dids';
        const { page, size, customer: _, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: (result, error, { customer }) => [
        { type: 'CustomerDids', id: customer },
      ],
    }),
    getPSTNUpgradeNumbers: builder.query({
      query: ({ page, size, ...params }) => {
        const { country, plan } = params;
        const url = `customer/dids/pstn-upgrade/available/countries/${country}/plans/${plan}`;
        return { url, params: { page, page_size: size } };
      },
      providesTags: (result, error, { customer }) => [
        { type: customer, tag: 'CustomerPSTNUpgradeDids' },
      ],
    }),
    getDetails: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}` };
      },
      providesTags: (result, error, { did }) => [
        { type: 'NumberDetails', did },
      ],
    }),
    updateDetails: builder.mutation({
      query(arg) {
        const { did, ...body } = arg;
        return {
          url: `customer/dids/${did}`,
          method: 'PATCH',
          body: body.data,
        };
      },
      invalidatesTags: (result, error, { did }) => [
        { type: 'NumberDetails', did },
      ],
    }),
    getStatistics: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/statistics` };
      },
      transformResponse: (response) => ({
        inbound: [
          { scope: 'Minutes last 24 hours', ...response.inbound?.[1] },
          { scope: 'Minutes last 7 days', ...response.inbound?.[7] },
          { scope: 'Minutes last 30 days', ...response.inbound?.[30] },
        ],
        outbound: [
          { scope: 'Minutes last 24 hours', ...response.outbound?.[1] },
          { scope: 'Minutes last 7 days', ...response.outbound?.[7] },
          { scope: 'Minutes last 30 days', ...response.outbound?.[30] },
        ],
      }),
    }),
    getLatestCDR: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `dids/${did}/statistics/cdrs/latest` };
      },
    }),
    getForServices: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/reachability-and-services` };
      },
    }),
    getOriginatingNetworks: builder.query({
      query: () => ({ url: `dids/originating-networks/for_select` }),
    }),
    getRouting: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/routing/routing-number` };
      },
      transformResponse: (response) => response.results,
      providesTags: ['RoutingNumber'],
    }),
    addRouting: builder.mutation({
      query(arg) {
        const { did, ...body } = arg;
        return {
          url: `customer/dids/${did}/routing/routing-number`,
          method: 'POST',
          body: body.data,
        };
      },
      invalidatesTags: ['RoutingNumber'],
    }),
    updateRouting: builder.mutation({
      query(arg) {
        const { did, ...body } = arg;
        return {
          url: `customer/dids/${did}/routing/routing-number`,
          method: 'PUT',
          body: body.data,
        };
      },
      invalidatesTags: ['RoutingNumber'],
    }),
    deleteRouting: builder.mutation({
      query(arg) {
        const { did, id } = arg;
        return {
          url: `customer/dids/${did}/routing/routing-number/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['RoutingNumber'],
    }),
    getShadowRouting: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/routing/shadow-routing-number` };
      },
      providesTags: (result, error, { did }) => [
        { type: 'ShadowRouting', did },
      ],
    }),
    addShadowRouting: builder.mutation({
      query(arg) {
        const { did, ...body } = arg;
        return {
          url: `customer/dids/${did}/routing/shadow-routing-number`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, { did }) => [
        { type: 'ShadowRouting', did },
      ],
    }),
    updateShadowRouting: builder.mutation({
      query(data) {
        const { did, id, ...body } = data;
        return {
          url: `customer/dids/${did}/routing/shadow-routing-number/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (result, error, { did }) => [
        { type: 'ShadowRouting', did },
      ],
    }),
    deleteShadowRouting: builder.mutation({
      query(arg) {
        const { did, id } = arg;
        return {
          url: `customer/dids/${did}/routing/shadow-routing-number/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, { did }) => [
        { type: 'ShadowRouting', did },
      ],
    }),
    getOutboundRates: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/outbound-rates` };
      },
    }),
    getForGeneral: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/general` };
      },
    }),
    getForDidGroups: builder.query({
      query: (arg) => {
        const { did } = arg;
        return { url: `customer/dids/${did}/did-group` };
      },
    }),
    applyForEndUserTransfer: builder.mutation({
      query(arg) {
        const { did, body } = arg;
        return {
          url: `customer/dids/${did}/end-user-transfer`,
          method: 'PATCH',
          body,
        };
      },
    }),
  }),
});

export const { useGetCustomerNumbersQuery } = numberApi;

export const getUserNumbers = createAsyncThunk(
  'numbers',
  async (_, thunkAPI) => {
    try {
      const res = await api({ thunkAPI }).get(`/customer/dids`);
      if (res.status === 200) {
        return res.data;
      }
      return thunkAPI.rejectWithValue(res.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const checkProfileForNumber = createAsyncThunk(
  'numbers/checkProfile',
  async ({ did, profileID }, thunkAPI) => {
    try {
      const res = await api({ thunkAPI }).get(
        `/numbers/${did}/checkProfile/${profileID}`
      );
      if (res.status === 200) {
        return res.data;
      }
      return thunkAPI.rejectWithValue(res.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getNumbersBoughtChannels = createAsyncThunk(
  'numbers/channels/bought',
  async (_, thunkAPI) => {
    try {
      const res = await api({ thunkAPI }).get(`/numbers/channels/bought`);
      if (res.status === 200) {
        return res.data;
      }
      return thunkAPI.rejectWithValue(res.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

const initialState = {
  dids: {
    isLoading: false,
    data: {
      count: 0,
      next: null,
      previous: null,
      results: [],
    },
  },
  didsChannels: { isLoading: false, data: [] },
  didsChannelsBought: { isLoading: false, data: [] },
  details: {
    isLoading: false,
    profile: {
      isLoading: false,
      id: null,
      profileName: '',
      data: {
        companyName: { required: null, value: '' },
        firstName: { required: null, value: '' },
        lastName: { required: null, value: '' },
        streetAddress: { required: null, value: '' },
        numberAddress: { required: null, value: '' },
        postalCode: { required: null, value: '' },
        city: { required: null, value: '' },
        country: { required: null, value: '' },
        phoneNumber: { required: null, value: '' },
        emailAddress: { required: null, value: '' },
        numberID: { required: null, value: '' },
        vatNumber: { required: null, value: '' },
        companyRegistrationNumber: { required: null, value: '' },
      },
      documents: {
        customerID: { required: null, active: null },
        lastInvoiceDocument: { required: null, active: null },
        proofOfAddress: { required: null, active: null },
      },
      type: { id: null, description: '' },
    },
    outbound: {
      plan: null,
      availability: { status: null, message: null },
      data: [],
    },
  },
  toast: {},
};

const numberSlice = createSlice({
  name: 'number',
  initialState,
  extraReducers: (builder) =>
    builder
      .addCase(getUserNumbers.pending, (state) => {
        state.dids.isLoading = true;
      })
      .addCase(getUserNumbers.fulfilled, (state, action) => {
        state.dids.isLoading = false;
        state.dids.data = action.payload;
      })
      .addCase(getUserNumbers.rejected, (state) => {
        state.dids.isLoading = false;
      })
      .addCase(checkProfileForNumber.pending, (state) => {
        state.details.profile.isLoading = true;
      })
      .addCase(checkProfileForNumber.fulfilled, (state, action) => {
        state.details.profile = { isLoading: false, ...action.payload };
      })
      .addCase(checkProfileForNumber.rejected, (state) => {
        state.details.profile.isLoading = false;
      })
      .addCase(getNumbersBoughtChannels.pending, (state) => {
        state.didsChannelsBought.isLoading = true;
      })
      .addCase(getNumbersBoughtChannels.fulfilled, (state, action) => {
        state.didsChannelsBought.isLoading = false;
        state.didsChannelsBought.data = action.payload;
      })
      .addCase(getNumbersBoughtChannels.rejected, (state) => {
        state.didsChannelsBought.isLoading = false;
      })
      .addMatcher(
        numberApi.endpoints.applyForEndUserTransfer.matchPending,
        (state, action) => {
          toastPendingPromise(
            state,
            action,
            `Your request has been sent to our backoffice.
            You will only see the reassigned profile after change has been approved by Backoffice. 
            This usually happens within 2 business days.`
          );
        }
      )
      .addMatcher(
        numberApi.endpoints.applyForEndUserTransfer.matchFulfilled,
        (state, action) => {
          toastFulfilledPromise(
            state,
            action,
            'End User transfer request was sent.'
          );
        }
      )
      .addMatcher(
        numberApi.endpoints.applyForEndUserTransfer.matchRejected,
        (state, action) => {
          toastRejectedPromise(state, action, {
            render:
              action.payload?.data ??
              'Please upload required documents before end-user transfer',
            type: 'error',
            isLoading: false,
            autoClose: 10000,
          });
        }
      ),
});

export default numberSlice.reducer;
